import React, { Dispatch, FC, SetStateAction } from 'react';
import { useTranslations } from 'next-intl';
import {
  CatButton,
  CatCard,
  CatIconShop,
  CatHeading
} from 'components/blocks-components';
import { Conditional } from 'components/pcc-components';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import GetGroupPartType from 'components/group-parts-type/group-parts-type';
import { LineItem } from 'types/parts-diagram';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import { revalidateCart } from 'services/parts-diagram/actions';
import FullMatchFitment from 'app/[lang]/[esite]/(core)/product/[product]/_components/full-match-fitment';
import { Image } from 'components/image';
import styles from '../parts-overview.module.scss';
import cx from 'classnames';
import PartCardPrice from '../part-card-price';
import PartsAvailabilityWrapper from 'components/parts-availability-wrapper';
import { PRODUCT_FALLBACK_IMAGE } from 'constants/common';

export interface PartsOverviewMobileCardProps {
  partItem: LineItem;
  selectedItem: string | null;
  onSelectItem: (referenceNumber: string | null) => void;
  handleOpen: (
    partNumber: string,
    price: ProductDealerPriceAvailability
  ) => Promise<void>;
  openQuickViewDrawer: Dispatch<SetStateAction<boolean>>;
  isSerialCompatible?: boolean;
  fromProductPage?: boolean;
  contactDealerBtn?: React.ReactNode;
  allCardsHasContactDealer?: boolean;
  isImageShown?: boolean;
  partsDiagramMobileView?: boolean;
  isPccPdpHeaderUpdateMobile?: boolean;
}

const PartsOverviewMobileCard: FC<PartsOverviewMobileCardProps> = ({
  partItem,
  selectedItem,
  onSelectItem,
  handleOpen,
  openQuickViewDrawer,
  isSerialCompatible,
  fromProductPage,
  contactDealerBtn,
  allCardsHasContactDealer,
  isImageShown = false,
  partsDiagramMobileView = false,
  isPccPdpHeaderUpdateMobile = false
}) => {
  const t = useTranslations();
  const {
    partNumber = '',
    name = '',
    referenceNumber = '',
    ieSystemControlNumber = null,
    disambiguation = false,
    priceAndAvailabilityData,
    quantity,
    imageIdentifiers
  } = partItem || {};

  const {
    isCatCorp,
    handleAddToCart,
    disableAddToCart,
    analyticsCommonInfo: { dealerData, model, currency } = {}
  } = usePartDiagram();

  const isArrangements = !!ieSystemControlNumber && !disambiguation;

  const analyticsInformation = {
    currency,
    model,
    dealerData
  };

  const handleAddItemToCart = () => {
    handleAddToCart(partItem, quantity, analyticsInformation);
    revalidateCart();
  };

  const isReplacedPart =
    priceAndAvailabilityData?.indirectReplacement ||
    (Array.isArray(priceAndAvailabilityData?.replacementParts) &&
      priceAndAvailabilityData?.replacementParts.length > 0);

  const iscurrentItemSelected = selectedItem === partItem.referenceNumber;
  const showAddtoCartButton =
    !isCatCorp && !isReplacedPart && priceAndAvailabilityData?.enableAddToCart;
  const showContactDealerbtn =
    !isCatCorp &&
    !priceAndAvailabilityData?.enableAddToCart &&
    !allCardsHasContactDealer;

  const handleOnClick = () => {
    if (!iscurrentItemSelected && partItem.referenceNumber) {
      return onSelectItem(partItem.referenceNumber);
    }
    onSelectItem(null);
  };
  return (
    <Conditional test={!!name} fallback={null}>
      <CatCard
        className={cx({
          [styles['parts-overview-mobile-card']]: !iscurrentItemSelected,
          [styles['parts-overview-mobile-card--selected']]:
            iscurrentItemSelected
        })}
        data-testid="part-mobile-card"
      >
        <div className="d-flex flex-column gap-3">
          <div
            className={cx(
              'd-flex flex-row',
              styles['parts-overview-mobile-card__content']
            )}
          >
            <Conditional test={!!referenceNumber} fallback={null}>
              <CatButton
                key={partItem.referenceNumber}
                onClick={handleOnClick}
                variant="ghost"
                className={cx(styles['reference-number__mobile-button'])}
                data-testid="part-mobile-card-button"
                size="sm"
              >
                <CatHeading
                  variant="body-sm"
                  className={cx({
                    [styles['reference-number__mobile']]:
                      !iscurrentItemSelected,
                    [styles['reference-number__mobile--selected']]:
                      iscurrentItemSelected
                  })}
                >
                  {referenceNumber}
                </CatHeading>
              </CatButton>
            </Conditional>
            <div className="d-flex align-items-center">
              {!isImageShown && (
                <Image
                  className={styles['parts-overview-mobile-card__image']}
                  src={imageIdentifiers || PRODUCT_FALLBACK_IMAGE}
                  alt={`${partNumber}: ${name}`}
                  width={80}
                  height={80}
                />
              )}
              <div className="d-flex flex-column">
                <GetGroupPartType
                  partNumber={partNumber}
                  name={name}
                  disambiguation={disambiguation}
                  ieSystemControlNumber={ieSystemControlNumber}
                  price={priceAndAvailabilityData}
                  handleOpen={handleOpen}
                  setDrawerActive={openQuickViewDrawer}
                  catHeadingClassName={styles['partsReference__text']}
                  catLinkSize="sm"
                  catTextCapitalize={true}
                />
                <PartCardPrice
                  currency={currency}
                  partItem={partItem}
                  className="m-0 p-0"
                  modifiedPriceData={priceAndAvailabilityData}
                  isMobileView
                  fromProductPage={fromProductPage}
                />
                <Conditional
                  test={
                    !isArrangements &&
                    !isCatCorp &&
                    priceAndAvailabilityData?.enableAddToCart
                  }
                >
                  <PartsAvailabilityWrapper
                    availabilityData={priceAndAvailabilityData}
                    shouldDisplayLabel={false}
                    isLoading={false}
                    isPccPdpHeaderUpdateMobile={isPccPdpHeaderUpdateMobile}
                    partsDiagramMobileView={partsDiagramMobileView}
                    openInDrawer={partsDiagramMobileView}
                    showAvailabilityAsBottomDrawer={true}
                  ></PartsAvailabilityWrapper>
                </Conditional>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-end">
            <div className="d-flex flex-column gap-1">
              <Conditional test={showAddtoCartButton}>
                <p className="cat-u-theme-typography-footnote m-0">
                  {t('SSRO_RECOMMENDEDQTY')} {quantity}
                </p>
              </Conditional>
              <Conditional test={isSerialCompatible}>
                <FullMatchFitment
                  className={styles['parts-overview-mobile-card__fitment-text']}
                  selectedEquipment={null}
                />
              </Conditional>
            </div>
            {!disambiguation && (
              <Conditional
                test={!!ieSystemControlNumber}
                fallback={
                  showAddtoCartButton && (
                    <CatButton
                      disabled={disableAddToCart}
                      onClick={() => handleAddItemToCart()}
                    >
                      {t('ADD')}
                      <CatIconShop slot="after" />
                    </CatButton>
                  )
                }
              />
            )}
            <Conditional test={showContactDealerbtn}>
              <div>{contactDealerBtn}</div>
            </Conditional>
          </div>
        </div>
      </CatCard>
    </Conditional>
  );
};

export default React.memo(PartsOverviewMobileCard);
